import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { SliderCheckbox } from '@components/common';
import { sortByChecked, sortByDefault } from '@components/common/ModalSelect/ModalSelectMenu/selectUtils';

import { TDynamicModalSelectOption, TValueType } from '../ModalSelect.types';
import { IModalSelectMenuTypes } from './ModalSelectMenu.types';

import Checkbox from '../../Checkbox';
import SearchInput from '../../SearchInput';
import SubmissionButtons from '../../SubmissionButtons';

import './ModalSelectMenu.scss';

const ModalSelectMenu = ({
	options,
	searchPlaceholder,
	searchThreshold,
	values,
	onChange,
	toggleSelectMenu,
	isViewMode = false,
}: IModalSelectMenuTypes) => {
	const [
		dynamicMenuOptions,
		setDynamicMenuOptions,
	] = useState<TDynamicModalSelectOption>(
		options.map((option, index) => ({
			...option,
			isHidden: false,
			isChecked: values ? values.map(({ value }) => value).includes(option.value as TValueType) : false,
			originalIndex: index,
		})));

	const [
		isAllItemsInMenuSelected,
		setIsAllItemsInMenuSelected,
	] = useState(values?.length ? options.length === values.length : false);

	const [searchValue, setSearchValue] = useState('');
	const [isCheckedSort, setIsCheckedSort] = useState(false);

	const [isIndefinite, setIsIndefinite] = useState(values?.length ? options.length > values.length : false);

	useEffect(() => {
		const allChecked = !dynamicMenuOptions.map(option => option.isChecked).includes(false);
		const someChecked = dynamicMenuOptions.map(option => option.isChecked).includes(true);

		setIsAllItemsInMenuSelected(allChecked);
		setIsIndefinite(allChecked ? false : someChecked);
	}, [dynamicMenuOptions]);

	const onResetHandler = () =>
		setDynamicMenuOptions(options.map((option, index) => ({
			...option,
			isHidden: false,
			isChecked: false,
			originalIndex: index,
		})));

	const onSaveHandler = () => {
		if (!onChange) return;

		onChange(dynamicMenuOptions.filter(option => option.isChecked));
		toggleSelectMenu();
	};

	const onSearchHandler = (value: string) => {
		const lowercaseValue = value.toLowerCase();

		setSearchValue(value);

		setDynamicMenuOptions(prevState => prevState.map(option =>
			({ ...option, isHidden: option.label?.toLowerCase().indexOf(lowercaseValue) === -1 })));
	};
	const toggleAllCheckbox = () => setDynamicMenuOptions(prevState => prevState.map(option => ({
		...option,
		isChecked: !isAllItemsInMenuSelected,
	})));

	const onChangeHandler = ({ value, isChecked }: { value: TValueType; isChecked: boolean }) => {
		setDynamicMenuOptions(prevState => prevState.map(option =>
			option.value === value
				? ({
					...option,
					isChecked: !isChecked,
				})
				: option,
		));

		setIsCheckedSort(false);
	};

	const onChangeSort = () => {
		setIsCheckedSort(prevState => {
			if (!prevState) {
				setDynamicMenuOptions(prevState => sortByChecked(prevState));
			} else setDynamicMenuOptions(prevState => sortByDefault(prevState));

			return !prevState;
		});
	};

	const isDisabledSave = useMemo(() => !dynamicMenuOptions.some(option => option.isChecked), [dynamicMenuOptions]);

	return (
		<div className='f5gModalSelectMenu'>
			<div className='f5gModalSelectMenuSort'>
				<SliderCheckbox
					checked={isCheckedSort}
					id='showSelectedOnTop'
					isLabelAfter
					label='Show selected on top'
					onChange={onChangeSort}
				/>
			</div>
			<div className='f5gModalSelectMenuSearch'>
				<SearchInput
					isClearable
					onSearch={onSearchHandler}
					placeholder={searchPlaceholder}
					searchThreshold={searchThreshold}
					value={searchValue}
				/>
			</div>
			<div className='f5gModalSelectMenuOptionsContainer'>
				{!isViewMode
					&& <div className='f5gModalSelectMenuAllOption'>
						<Checkbox
							checked={isAllItemsInMenuSelected}
							isIndefinite={isIndefinite}
							label='All'
							name='all'
							onChange={toggleAllCheckbox}
						/>
					</div>
				}
				{dynamicMenuOptions.map(({ label, value, isHidden, isChecked }, index) => {
					const onChangeItem = () => onChangeHandler({ value, isChecked });

					return (
						<div
							key={value.toString() + index}
							className={cn('f5gModalSelectMenuCheckboxOption', { 'f5gModalSelectMenuCheckboxOptionHidden': isHidden })}
						>
							<Checkbox
								checked={isViewMode || isChecked}
								disabled={isViewMode}
								label={label}
								name={value.toString()}
								onChange={onChangeItem}
							/>
						</div>
					);
				})}
			</div>
			{!isViewMode
				&& <div className='f5gModalSelectMenuButtons'>
					<SubmissionButtons
						disabledPositive={isDisabledSave}
						negativeHandler={onResetHandler}
						negativeLabel='Reset all'
						position='right'
						positiveHandler={onSaveHandler}
						positiveLabel='Save'
					/>
				</div>
			}
		</div>
	);
};

export default ModalSelectMenu;
